import type React from 'react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

export interface EntityFeatureBarItem<T extends string = string> {
  value: T;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  hasAttention?: boolean;
}

export interface EntityFeatureBarProps<T extends string = string> {
  items: EntityFeatureBarItem<T>[];
  activeTab: T;
  onSelectTab: (tab: T) => void;
  className?: string;
}

export function EntityFeatureBar<T extends string = string>({
  items,
  activeTab,
  onSelectTab,
  className,
}: EntityFeatureBarProps<T>) {
  if (items.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={cn(
          'flex items-center justify-between gap-0.5 w-full p-1 rounded-xl bg-background/90 border border-border/60 shadow-2xs',
          className
        )}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.value;

          return (
            <Tooltip key={item.value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onSelectTab(item.value)}
                  className={cn(
                    'relative flex-1 flex flex-col items-center justify-center py-1.5 px-0.5 rounded-lg transition-all text-xs font-medium cursor-pointer select-none outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    isActive
                      ? 'bg-primary/15 text-primary border border-primary/30 shadow-xs'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent'
                  )}
                  aria-label={item.title}
                >
                  <Icon
                    className={cn(
                      'w-3.5 h-3.5 transition-transform duration-150',
                      isActive && 'scale-110'
                    )}
                  />
                  <span className="text-[9px] font-medium leading-tight mt-0.5 truncate max-w-full">
                    {item.title}
                  </span>

                  {item.badge !== undefined && item.badge !== 0 && (
                    <span
                      className={cn(
                        'absolute -top-1 -right-0.5 min-w-3.5 h-3.5 px-1 rounded-full text-[9px] font-bold flex items-center justify-center leading-none shadow-2xs pointer-events-none',
                        item.hasAttention
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground border border-border/80'
                      )}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="center"
                sideOffset={6}
                className="!bg-popover !text-popover-foreground border border-border/80 shadow-lg max-w-[210px] text-center p-2.5 rounded-xl [&_svg]:!fill-popover [&_svg]:!bg-popover"
              >
                <p className="font-semibold text-xs text-popover-foreground">{item.title}</p>
                <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                  {item.description}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
